import React from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const COLORS = {
  target: "#26BF00",
  current: "#2E93fA",
  integrated: "#A53838",
  forecast: "#DC6CFE",
  geyser: "#FDB940",
};

const darkBg = "#13171f";
const gridColor = "#1e2330";
const labelColor = "#b0b8c4";

function makeGradient(color) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: color + "55" },
    { offset: 1, color: color + "05" },
  ]);
}

function baseOption(series) {
  return {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 32, right: 80, bottom: 56, left: 64, containLabel: false },
    dataZoom: [{ type: "inside", xAxisIndex: 0, filterMode: "filter" }],
    toolbox: {
      right: 8, top: 4,
      feature: {
        dataZoom: { yAxisIndex: "none", title: { zoom: "Zoom", back: "Reset" }, iconStyle: { borderColor: "#b0b8c4" }, emphasis: { iconStyle: { borderColor: "#2E93fA" } } },
        restore: { title: "Reset", iconStyle: { borderColor: "#b0b8c4" }, emphasis: { iconStyle: { borderColor: "#2E93fA" } } },
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1e2330",
      borderColor: "#2a2f3d",
      textStyle: { color: "#e0e6f0", fontSize: 12 },
      formatter: (params) =>
        params
          .filter((p) => p.value !== null && p.value !== undefined)
          .map(
            (p) =>
              `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px"></span>` +
              `${p.seriesName}: <b>${Number(p.value[1]).toFixed(3)} MVA</b>`
          )
          .join("<br/>"),
    },
    legend: {
      top: 0,
      textStyle: { color: labelColor, fontSize: 11 },
      icon: "circle",
      itemWidth: 10,
      itemHeight: 10,
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: { color: labelColor, fontSize: 10, formatter: (v) => {
        const d = new Date(v);
        return `${d.getHours().toString().padStart(2,"0")}:${d.getMinutes().toString().padStart(2,"0")}`;
      }},
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10, formatter: (v) => v.toFixed(2) },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series,
  };
}

function lineSeries(name, color, data, dashed = false) {
  return {
    name,
    type: "line",
    smooth: true,
    symbol: "none",
    lineStyle: { color, width: 2, type: dashed ? "dashed" : "solid" },
    areaStyle: { color: makeGradient(color), opacity: 0.4 },
    itemStyle: { color },
    data: data.filter(([, y]) => y !== null),
  };
}

function toTs(items, valueFn) {
  return items.map((item) => [new Date(item.interval).getTime(), valueFn(item)]);
}

function EmptyState() {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#5a6070" }}>No data available</Typography>
    </Box>
  );
}

export default function EChartsTemplate2({ microServiceGraphData, targetDataNow, microserviceForcastState }) {
  const hasData = microServiceGraphData.length > 0;

  const demandSeries = [
    lineSeries("Target Demand", COLORS.target,
      toTs(microServiceGraphData, () => targetDataNow?.target ? targetDataNow.target / 1000000 : null), true),
    lineSeries("Current Demand", COLORS.current,
      toTs(microServiceGraphData, (i) => i.current_demand ? i.current_demand / 1000000 : null)),
    lineSeries("Integrated Demand", COLORS.integrated,
      toTs(microServiceGraphData, (i) => i.latest_integrated_demand ? i.latest_integrated_demand / 1000000 : null)),
    lineSeries("Forecast", COLORS.forecast,
      toTs(microServiceGraphData, () => microserviceForcastState?.[0]?.forecast ? microserviceForcastState[0].forecast / 1000000 : null), true),
  ];

  const geyserSeries = [
    lineSeries("Geyser Demand", COLORS.geyser,
      toTs(microServiceGraphData, (i) => i.geyser_demand ? i.geyser_demand / 1000000 : null)),
  ];

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: labelColor, mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Demand Overview — MVA
        </Typography>
        <Box sx={{ bgcolor: darkBg, borderRadius: 2, overflow: "hidden" }}>
          {hasData
            ? <ReactECharts option={baseOption(demandSeries)} style={{ height: 380 }} notMerge />
            : <Box sx={{ height: 380 }}><EmptyState /></Box>}
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: labelColor, mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Geyser (Tjommie) Demand — MVA
        </Typography>
        <Box sx={{ bgcolor: darkBg, borderRadius: 2, overflow: "hidden" }}>
          {hasData
            ? <ReactECharts option={baseOption(geyserSeries)} style={{ height: 380 }} notMerge />
            : <Box sx={{ height: 380 }}><EmptyState /></Box>}
        </Box>
      </Box>
    </>
  );
}
